/**
 * Season filtering utility for the two-digit-year-span format sellers use
 * for "Saison" (e.g., "99/00" for the 1999-2000 season). Both sides are
 * years, not a month - confirmed with the client: sellers can pick
 * arbitrary spans (e.g. "93/99"), not just consecutive years, so any
 * two-digit/two-digit combination is a legitimate value. (This file
 * previously validated the second half as a month 01-12, which silently
 * rejected the vast majority of real season values from the filter
 * dropdown - only combinations where the second year happened to be <= 12
 * ever passed.)
 */

/**
 * Validate season format: YY/YY (e.g., 99/00 for the 1999-2000 season)
 * @param {string} season - Season string to validate
 * @returns {boolean} - True if valid format, false otherwise
 */
export const isValidSeasonFormat = (season) => {
  if (!season || typeof season !== 'string') {
    return false;
  }

  const trimmed = season.trim();

  // Check format: YY/YY (e.g., 99/00) - both sides are two-digit years,
  // any combination is valid (see file header).
  const seasonRegex = /^\d{2}\/\d{2}$/;
  if (!seasonRegex.test(trimmed)) {
    return false;
  }

  const [startStr, endStr] = trimmed.split('/');
  const startYear = parseInt(startStr, 10);
  const endYear = parseInt(endStr, 10);

  // Validate both are two-digit years: 00-99 (representing 1900-2099)
  return startYear >= 0 && startYear <= 99 && endYear >= 0 && endYear <= 99;
};

/**
 * Convert a YY/YY season span to its two full years.
 * Assumes years 00-50 are 2000-2050, 51-99 are 1951-1999 (same rule
 * applied independently to each side, since a span can cross the century
 * boundary, e.g. "99/00").
 * @param {string} season - Season string (e.g., "99/00")
 * @returns {object|null} - { startYear, endYear, fullStartYear, fullEndYear, formatted }
 */
export const parseSeasonFormat = (season) => {
  if (!isValidSeasonFormat(season)) {
    return null;
  }

  const [startStr, endStr] = season.trim().split('/');
  const startYear = parseInt(startStr, 10);
  const endYear = parseInt(endStr, 10);

  const toFullYear = yy => (yy <= 50 ? 2000 + yy : 1900 + yy);
  const fullStartYear = toFullYear(startYear);
  const fullEndYear = toFullYear(endYear);

  return {
    startYear,
    endYear,
    fullStartYear,
    fullEndYear,
    formatted: `${fullStartYear}/${fullEndYear}`,
  };
};

/**
 * Filter season options by valid format.
 * Options have structure: { option: id, label: "YY/YY" }
 * @param {Array} options - Array of season option objects
 * @returns {Array} - Filtered array of valid season options
 */
export const filterValidSeasons = (options) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter(opt => isValidSeasonFormat(opt?.label));
};

/**
 * Sort season options chronologically (by start year, then end year).
 * @param {Array} options - Array of season option objects
 * @param {string} order - 'asc' for ascending (oldest first), 'desc' for descending (newest first)
 * @returns {Array} - Sorted array of season options
 */
export const sortSeasonOptions = (options, order = 'asc') => {
  if (!Array.isArray(options)) {
    return [];
  }

  const sorted = [...options].sort((a, b) => {
    const parsedA = parseSeasonFormat(a?.label);
    const parsedB = parseSeasonFormat(b?.label);

    if (!parsedA || !parsedB) {
      return 0;
    }

    const timeA = parsedA.fullStartYear * 100 + parsedA.fullEndYear;
    const timeB = parsedB.fullStartYear * 100 + parsedB.fullEndYear;

    return order === 'asc' ? timeA - timeB : timeB - timeA;
  });

  return sorted;
};

/**
 * Given a typed search word, check whether it's a full 4-digit year (e.g.
 * "1999") and if so, return the two-digit suffix ("99") sellers actually
 * use in the "Saison" field. Lets a search for "1999" find seasons like
 * "93/99" or "99/00" that include that year, even though "1999" itself
 * never appears literally in a "YY/YY" value.
 * @param {string} word - Typed search token
 * @returns {string|null} - Two-digit year suffix, or null if not a 4-digit year
 */
export const yearToSeasonSuffix = (word) => {
  const match = /^(?:19|20)(\d{2})$/.exec(`${word || ''}`.trim());
  return match ? match[1] : null;
};

/**
 * Whether a season value (e.g. "93/99") includes the given two-digit year
 * suffix on either side of the slash.
 * @param {string} season - Season string, e.g. "93/99"
 * @param {string} yearSuffix - Two-digit year suffix, e.g. "99"
 * @returns {boolean}
 */
export const seasonIncludesYearSuffix = (season, yearSuffix) => {
  if (!yearSuffix || !season || typeof season !== 'string') {
    return false;
  }
  return season
    .trim()
    .split('/')
    .map(part => part.trim())
    .includes(yearSuffix);
};

export default {
  isValidSeasonFormat,
  parseSeasonFormat,
  filterValidSeasons,
  sortSeasonOptions,
  yearToSeasonSuffix,
  seasonIncludesYearSuffix,
};
